// EvolutionChart — shared multi-series SVG time-series (house pattern:
// hand-rolled polyline + circles, Progress.tsx energy-trend lineage).
// Extracted from FitnessBodyScan so Fitness Progress and Body Scan render
// the exact same chart instead of duplicating SVG math. Each series is
// normalized to its own min/max (different units share one canvas — the
// chart shows SHAPE/trend; absolute values live in the surrounding UI).
// Period filtering is the caller's job: pass already-filtered values.

export interface EvolutionSeries {
  key: string;
  color: string;
  label: string;
  // Chronologically ascending samples: t = epoch ms, v = value.
  values: Array<{ t: number; v: number }>;
}

const VIEW_W = 280;
const VIEW_H = 80;
const PAD_TOP = 6;
const PAD_BOTTOM = 6;

export function EvolutionChart({ series }: { series: EvolutionSeries[] }) {
  const drawable = series.map((s) => {
    if (s.values.length === 0) return { ...s, points: [] as Array<{ x: number; y: number }> };
    const t0 = s.values[0].t;
    const t1 = s.values[s.values.length - 1].t;
    const vMin = Math.min(...s.values.map((p) => p.v));
    const vMax = Math.max(...s.values.map((p) => p.v));
    const span = vMax - vMin || 1;
    const usable = VIEW_H - PAD_TOP - PAD_BOTTOM;
    const points = s.values.map((p) => ({
      x: t1 === t0 ? VIEW_W / 2 : ((p.t - t0) / (t1 - t0)) * VIEW_W,
      y: VIEW_H - PAD_BOTTOM - ((p.v - vMin) / span) * usable,
    }));
    return { ...s, points };
  });

  return (
    <>
      <svg width="100%" height={VIEW_H} viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} preserveAspectRatio="none" className="overflow-visible">
        {[0, VIEW_H / 2, VIEW_H].map((y) => (
          <line key={y} x1="0" y1={y} x2={VIEW_W} y2={y} stroke="hsl(222 18% 20%)" strokeWidth="0.5" />
        ))}
        {drawable.map((s) =>
          s.points.length >= 2 ? (
            <polyline key={s.key} fill="none" stroke={s.color} strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"
              points={s.points.map((p) => `${p.x},${p.y}`).join(" ")} />
          ) : null,
        )}
        {drawable.map((s) =>
          s.points.map((p, i) => (
            <circle key={`${s.key}-${i}`} cx={p.x} cy={p.y} r="3" fill={s.color} stroke="hsl(222 38% 6%)" strokeWidth="1.5" />
          )),
        )}
      </svg>
      <div className="flex gap-4 mt-2">
        {drawable.filter((s) => s.points.length > 0).map((s) => (
          <span key={s.key} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </>
  );
}
